/**
 * useClippingsHeader - 剪藏簿特殊页面 ContentHeader 配置 Hook
 * 统一管理博主列表、未分类、已加星标、视频页面的 header 配置
 */

import { useState, useMemo } from 'react';
import type { SortBy } from '../utils/postList';

/** 页面类型 */
export type ClippingsHeaderType = 'bloggers' | 'uncategorized' | 'starred' | 'video';

/** 页面配置 */
interface HeaderConfig {
  title: string;
  searchPlaceholder: string;
  showSortDropdown: boolean;
}

/** 集中配置 - 修改此处即可影响所有页面 */
const HEADER_CONFIGS: Record<ClippingsHeaderType, HeaderConfig> = {
  bloggers: {
    title: '已追踪博主列表',
    searchPlaceholder: '搜索博主或标签',
    showSortDropdown: true,
  },
  uncategorized: {
    title: '未分类',
    searchPlaceholder: '搜索标题、博主或标签',
    showSortDropdown: true,
  },
  starred: {
    title: '已加星标',
    searchPlaceholder: '搜索标题、博主或标签',
    showSortDropdown: true,
  },
  video: {
    title: '视频帖子',
    searchPlaceholder: '搜索标题、博主或标签',
    showSortDropdown: true,
  },
};

/** Hook 返回值 */
export interface UseClippingsHeaderReturn {
  /** 传递给 ContentHeader 的 props */
  headerProps: {
    title: string;
    searchValue: string;
    onSearchChange: (value: string) => void;
    searchPlaceholder: string;
    showSortDropdown: boolean;
    sortBy: SortBy;
    onSortChange: (sortBy: SortBy) => void;
  };
  /** 当前搜索关键词（用于过滤列表） */
  searchKeyword: string;
  /** 当前排序方式（用于排序列表） */
  sortBy: SortBy;
}

/**
 * 剪藏簿特殊页面 ContentHeader 配置 Hook
 * @param type 页面类型
 * @param defaultSortBy 默认排序方式，默认为 'time'
 */
export function useClippingsHeader(
  type: ClippingsHeaderType,
  defaultSortBy: SortBy = 'time'
): UseClippingsHeaderReturn {
  const [searchKeyword, setSearchKeyword] = useState('');
  const [sortBy, setSortBy] = useState<SortBy>(defaultSortBy);

  const config = HEADER_CONFIGS[type];

  const headerProps = useMemo(() => ({
    title: config.title,
    searchValue: searchKeyword,
    onSearchChange: setSearchKeyword,
    searchPlaceholder: config.searchPlaceholder,
    showSortDropdown: config.showSortDropdown,
    sortBy,
    onSortChange: setSortBy,
  }), [config, searchKeyword, sortBy]);

  return {
    headerProps,
    searchKeyword,
    sortBy,
  };
}

export default useClippingsHeader;
